// One scope resolution for the dashboard containers: which organization, which
// school year, and how far back the enrollment ledger can answer for.
//
// Shared by DistrictDashboard and RenewalBrief so there is exactly one copy of
// it. Per-container copies drift, and the failure mode is specific: URL mirroring
// done with `window.history.replaceState` updates the address bar without telling
// react-router anything, so every consumer reading the router's own location goes
// stale the moment a control is touched. Fixing that in one copy and not the other
// is the argument for there being one copy.
//
// What lives here is what both containers need identically: the org pick and its
// deep-link resolution, the school-year state and its URL mirror, and the
// provenance trio (how far back the district and the ledger reach -> which years
// are offered and which of those are selectable -> what to call the year on
// screen). What is NOT here is `windowSpec`: the two containers legitimately want
// different windows -- the dashboard reads ?interval= and ?end= overrides and
// derives a separate utilInterval, the brief is always monthly over a whole year --
// so each builds its own from the `schoolYear` / `curSY` / `today` this returns.
// Forcing that into a shared shape would mean a hook parameter per difference.
import { useEffect, useMemo, useState } from "react";
import config from "../../common/config";
import { currentSchoolYear, syYearOptions } from "../impact/schoolyear";
import { ALL_ORGS, ALL_ORGS_ID, isAllOrgs } from "../impact/chrome";

const useDashboardScope = ({ api, user, history }) => {
  // View state is mirrored into the query string -- React state stays the source
  // of truth, the URL is a synced mirror -- so any view is a shareable deep link.
  // `history.replace` keeps it out of the back button.
  //
  // Router `history`, NOT window.history.replaceState. replaceState silently
  // drops any param set after mount: pick a past school year, click another tab,
  // and you land on the current year's default view, because DashShell builds its
  // nav links from useLocation().search and never sees the change.
  const syncParams = (updates) => {
    const qp = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") qp.delete(key);
      else qp.set(key, value);
    });
    const qs = qp.toString();
    history.replace({
      pathname: window.location.pathname,
      search: qs ? `?${qs}` : "",
    });
  };

  // ---- organization --------------------------------------------------------
  // Org admins are pinned to their own org; superadmins pick one, seeded from
  // ?organization= so a picked org is linkable.
  //
  // A superadmin with no ?organization= lands on ALL_ORGS rather than on an
  // empty page. The page used to render nothing at all until a district was
  // picked, which made the most privileged account the only one that opens the
  // dashboard on a blank screen -- and there is a real question the blank screen
  // was refusing to answer ("how is the whole customer base doing?"). It costs a
  // slower first load; see the note on the all-orgs scope in DistrictDashboard.
  const isSuperadmin = user && user.usertype === config.SUPERADMIN_USERTYPE;
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const idStr = new URLSearchParams(window.location.search).get(
      "organization"
    );
    if (idStr === ALL_ORGS_ID) return ALL_ORGS;
    const id = idStr ? parseInt(idStr, 10) : NaN;
    return Number.isNaN(id) ? ALL_ORGS : { id, name: null };
  });

  // When the district was created, which is how far back the year picker reaches
  // (see syOrgFloor). It arrives on the windowed stats responses, alongside the
  // ledger fields below, but lives here because it is a property of the org rather
  // than of the ledger -- an org with no enrollment intervals at all still has a
  // creation date to floor the list with, and the two have to be cleared on
  // different events.
  const [orgCreated, setOrgCreated] = useState(null);

  // `extra` lets a container mirror its own params in the same history entry --
  // the dashboard clears ?school= when the org changes, since a school id means
  // nothing in the new district.
  const selectOrg = (org, extra = {}) => {
    setSelectedOrg(org);
    // The previous district's creation date must not survive the switch: it floors
    // the school-year picker, so carrying it over offers the new district years it
    // may not have existed for until its first response lands.
    setOrgCreated(null);
    syncParams({
      organization: org && org.id ? org.id : null,
      ...extra,
    });
  };

  // A deep-linked ?organization= arrives as an id with no name, which would leave
  // the picker labelled blank. Resolve it once.
  useEffect(() => {
    if (
      !isSuperadmin ||
      !selectedOrg ||
      !selectedOrg.id ||
      selectedOrg.name ||
      isAllOrgs(selectedOrg)
    )
      return undefined;
    let cancelled = false;
    api
      .fetchOrganizationInfo(selectedOrg.id)
      .then((o) => {
        if (!cancelled && o && o.name)
          setSelectedOrg((cur) =>
            cur && cur.id === o.id ? { ...cur, name: o.name } : cur
          );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg && selectedOrg.id]);

  const org = isSuperadmin ? selectedOrg : user && user.organization;

  // ---- school year ---------------------------------------------------------
  const today = useMemo(() => new Date(), []);
  const curSY = currentSchoolYear(today);
  const [schoolYear, setSchoolYear] = useState(() => {
    const s = new URLSearchParams(window.location.search).get("start");
    const y = s ? parseInt(s.slice(0, 4), 10) : NaN;
    return Number.isNaN(y) ? curSY : y;
  });

  const selectSchoolYear = (y) => {
    setSchoolYear(y);
    // The current year is the default, so it needs no ?start=. For a past year,
    // also drop a stale ?end= carried in from a current-year link -- it would clip
    // the past year short. (Seeding parses slice(0,4) and the containers derive
    // `${schoolYear}-08-01`, so state and URL agree either way.)
    if (y === curSY) syncParams({ start: null });
    else syncParams({ start: `${y}-08-01`, end: null });
  };

  // ---- roster provenance ---------------------------------------------------
  // How far back rosters can be reconstructed. Comes back with every windowed
  // stats payload, so it is null until the first response lands and the picker
  // offers only the current year until then.
  const [ledgerStart, setLedgerStart] = useState(null);
  const [serverCoverage, setServerCoverage] = useState(null);
  // Which breakdown dimensions the ledger can answer for the window on screen.
  // Null until a response lands. Separate from `coverage` on purpose: the edges did
  // not all start on the same day, so a window whose roster is reconstructable can
  // still be one whose teacher attribution is not -- see the backend's
  // dimension_coverages.
  const [dimensionsAvailable, setDimensionsAvailable] = useState(null);

  const yearOptions = useMemo(
    () => syYearOptions(curSY, schoolYear, ledgerStart, today, orgCreated),
    [curSY, schoolYear, ledgerStart, today, orgCreated]
  );

  // Provenance of the year on screen, for the caveat under the nav.
  //
  // The server's own `coverage` wins when we have it: the local computation
  // compares dates in the browser's timezone against a UTC ledger instant, so at a
  // boundary the two can disagree -- and the direction that matters (suppressing
  // the caveat on a year the backend actually reconstructed) is the one we can't
  // accept. The local computation still drives the *other* years in the picker,
  // which have no response to consult.
  const coverage = useMemo(() => {
    if (serverCoverage) return serverCoverage;
    const hit = yearOptions.find((o) => o.year === schoolYear);
    return hit ? hit.coverage : "full";
  }, [serverCoverage, yearOptions, schoolYear]);

  // Roster provenance rides on EVERY windowed response and they all describe the
  // same window, so take it from whichever lands first rather than waiting on a
  // barrier. The year picker is built from ledger_start, so deferring it to the
  // slowest request meant every past year read "unavailable" while that request
  // finished -- which reads as "this year has no data" rather than "still loading".
  // Later responses re-report identical values, so the extra setState calls settle
  // rather than fight.
  const noteLedger = (r) => {
    if (r && r.ledger_start) {
      setLedgerStart(r.ledger_start);
      setServerCoverage(r.coverage || null);
      if (r.dimensions_available) setDimensionsAvailable(r.dimensions_available);
    }
    // Read outside the ledger_start guard on purpose. The two travel together but
    // are not the same fact, and a district with no enrollment intervals at all
    // reports a null ledger_start while still having a creation date — gating this
    // on the ledger would leave exactly those orgs with the fixed four-year list.
    if (r && r.org_created) setOrgCreated(r.org_created);
    return r;
  };

  // A load starting on a new year must clear the previous year's verdict: the
  // caveat renders outside the swapped-out body, so a stale one keeps describing
  // the year that just left the screen.
  const resetCoverage = () => {
    setServerCoverage(null);
    setDimensionsAvailable(null);
  };

  // Deliberately narrow: `noteLedger` and `resetCoverage` are the whole interface
  // to roster provenance, and the raw setters are not exposed. A container that
  // sets ledgerStart by hand is a container that can set it from something other
  // than a windowed response, and that is how consumers drift apart.
  return {
    syncParams,
    isSuperadmin,
    selectedOrg,
    selectOrg,
    org,
    today,
    curSY,
    schoolYear,
    selectSchoolYear,
    yearOptions,
    coverage,
    dimensionsAvailable,
    noteLedger,
    resetCoverage,
  };
};

export { useDashboardScope };
